// The energy of each side, in one row under the captured pieces. A side pays
// for a roll with it. The cost of a roll for the player ends the row.
// The tooltip of a count has the cost for that side.

import { OWNER_NAMES } from '../engine/describe';
import type { Side } from '../engine/types';
import { energyOf, type GameState, rollCost } from '../game/state';
import { el } from '../lib/dom';
import { localSide } from '../state/localSide';
import { subscribeView, viewedGame } from '../state/view';
import { createSideMark } from './SideMark';

const SIDES: readonly Side[] = ['gold', 'violet'];
/** Plain words, not final copy. */
const ENERGY_LABEL = 'Energy';

export const createEnergyRow = (): HTMLElement => {
  const counts = new Map(
    SIDES.map((side) => [side, el('span', { className: 'energy__count' })] as const),
  );
  // The cost of a roll for the player, at the end of the row. The tooltip has the cost for Moon.
  const cost = el('span', { className: 'energy__cost' });
  const sides = SIDES.map((side) =>
    el('span', { className: `energy__side energy__side--${side}` }, [
      createSideMark(side),
      counts.get(side) ?? '',
    ]),
  );
  const root = el('div', { className: 'energy' }, [
    el('span', { className: 'energy__label', textContent: ENERGY_LABEL }),
    ...sides,
    cost,
  ]);

  const draw = (state: GameState): void => {
    // In Godsmode a skill has its own cost, on its slot.
    cost.hidden = state.mode === 'gods';
    cost.textContent = `(casts cost ${rollCost(state, localSide())})`;
    SIDES.forEach((side, index) => {
      const count = counts.get(side);
      const chip = sides[index];
      if (!count || !chip) return;
      count.textContent = String(energyOf(state, side));
      chip.setAttribute('aria-label', `${OWNER_NAMES[side]}: ${energyOf(state, side)}`);
      chip.dataset.tip =
        state.mode === 'gods'
          ? OWNER_NAMES[side]
          : `${OWNER_NAMES[side]}. A cast costs ${rollCost(state, side)}.`;
    });
  };

  draw(viewedGame());
  subscribeView(() => draw(viewedGame()));
  return root;
};
